import React, { useState } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ClientSelector({ clients, selectedClient, onClientSelect, onClientCreate }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [invitationLink, setInvitationLink] = useState(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newClientName.trim()) {
      onClientCreate(newClientName, newClientEmail);
      setNewClientName('');
      setNewClientEmail('');
      setShowNewClientForm(false);
      setShowDropdown(false);
    }
  };

  const handleSendInvitation = async (clientId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API_URL}/clients/${clientId}/send-invitation`);
      const signupUrl = `${window.location.origin}/client/signup?code=${response.data.invitationCode}`;
      setInvitationLink(signupUrl);
      setShowInvitationModal(true);
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(invitationLink);
    toast.success('Invitation link copied to clipboard!');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="bg-[var(--bg-secondary)] hover:bg-[var(--border-color)] text-white px-6 py-2.5 rounded-lg transition-all border border-[var(--border-color)] flex items-center gap-3 font-medium"
      >
        {selectedClient ? (
          <>
            <span className="font-medium">{selectedClient.name}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        ) : (
          <>
            <span>Select Client</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-[var(--bg-secondary)] rounded-lg shadow-xl border border-[var(--border-color)] overflow-hidden z-50">
          <div className="p-4 border-b border-[var(--border-color)]">
            <button
              onClick={() => setShowNewClientForm(!showNewClientForm)}
              className="w-full bg-[var(--accent)] hover:bg-[var(--accent)] text-white px-4 py-2.5 rounded-lg transition-all font-semibold"
            >
              {showNewClientForm ? 'Cancel' : '+ New Client'}
            </button>
          </div>

          {showNewClientForm && (
            <form onSubmit={handleSubmit} className="p-4 border-b border-[var(--border-color)] bg-[var(--bg-tertiary)]">
              <input
                type="text"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                className="w-full bg-[var(--bg-primary)] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg mb-3 border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 font-medium"
                placeholder="Client name"
                required
              />
              <input
                type="email"
                value={newClientEmail}
                onChange={(e) => setNewClientEmail(e.target.value)}
                className="w-full bg-[var(--bg-primary)] text-white placeholder-gray-500 px-4 py-2.5 rounded-lg mb-3 border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 font-medium"
                placeholder="Email (optional)"
              />
              <button
                type="submit"
                className="w-full bg-[var(--accent)] hover:bg-[var(--accent)] text-white px-4 py-2.5 rounded-lg transition-all font-semibold"
              >
                Create
              </button>
            </form>
          )}

          <div className="max-h-96 overflow-y-auto">
            {clients.map((client) => (
              <div
                key={client.id}
                className={`flex items-center justify-between px-4 py-3 hover:bg-[var(--border-color)] transition-all ${
                  selectedClient?.id === client.id ? 'bg-[var(--accent)]/10' : ''
                }`}
              >
                <button
                  onClick={() => {
                    onClientSelect(client);
                    setShowDropdown(false);
                  }}
                  className="flex-1 text-left"
                >
                  <div className="font-medium text-white">{client.name}</div>
                  {client.email && <div className="text-sm text-gray-400">{client.email}</div>}
                </button>
                <button
                  onClick={(e) => handleSendInvitation(client.id, e)}
                  className="ml-2 px-3 py-1.5 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-xs rounded-lg transition-all"
                  title="Send invitation link"
                >
                  Invite
                </button>
              </div>
            ))}
            {clients.length === 0 && !showNewClientForm && (
              <div className="p-8 text-center text-gray-400">
                <p>No clients yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invitation Modal */}
      {showInvitationModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowInvitationModal(false)}>
          <div className="bg-[var(--bg-secondary)] rounded-lg p-6 max-w-md w-full mx-4 border border-[var(--border-color)] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-4">Client Invitation Link</h3>
            <p className="text-gray-300 mb-4 text-sm">Share this link with your client to allow them to create their account:</p>
            <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-3 mb-4 break-all text-sm text-gray-300">
              {invitationLink}
            </div>
            <div className="flex gap-3">
              <button
                onClick={copyToClipboard}
                className="flex-1 bg-[var(--accent)] hover:bg-[var(--accent)] text-white px-4 py-2 rounded-lg transition-all font-semibold"
              >
                Copy Link
              </button>
              <button
                onClick={() => setShowInvitationModal(false)}
                className="px-4 py-2 bg-[var(--bg-tertiary)] hover:bg-[var(--border-color)] text-white rounded-lg transition-all border border-[var(--border-color)] font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientSelector;
